/**
 * Firebase Cloud Messaging Notification Service
 * يستخدم هذا الملف بدلاً من Expo Push Notifications
 *
 * التغييرات الرئيسية:
 * - استبدال Expo Push بـ Firebase Cloud Messaging (FCM)
 * - استخدام @notifee/react-native لعرض الإشعارات المحسن
 * - دعم أفضل للإشعارات في background و killed state
 */

import notifee, { AndroidImportance, EventType } from '@notifee/react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import messaging from '@react-native-firebase/messaging';
import * as Device from 'expo-device';
import { Platform } from 'react-native';
import { API_BASE_URL } from '../config/api';
import logger from '../utils/logger';
import { getStoredTokens } from '../utils/storage';

export interface NotificationPreferences {
  alarmsEnabled: boolean;
  lowBatteryEnabled: boolean;
  deviceOfflineEnabled: boolean;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart: string; // HH:MM format
  quietHoursEnd: string; // HH:MM format
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  alarmsEnabled: true,
  lowBatteryEnabled: true,
  deviceOfflineEnabled: true,
  soundEnabled: true,
  vibrationEnabled: true,
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '07:00',
};

class NotificationService {
  private fcmToken: string | null = null;
  private channelsCreated: boolean = false;
  private tapCallback: ((data: any) => void) | null = null;
  // ✅ FIX: Track listener unsubscribes to prevent duplicate listeners on logout/login cycles
  private messageListenerUnsubscribe: (() => void) | null = null;
  private tokenRefreshUnsubscribe: (() => void) | null = null;

  /**
   * Get current FCM token
   */
  getFcmToken(): string | null {
    return this.fcmToken;
  }

  /**
   * Initialize the notification service
   * Call this early in app startup
   */
  async init(): Promise<void> {
    try {
      await this.createNotificationChannels();
      logger.log('✅ Notification service initialized');
    } catch (error) {
      logger.error('Failed to initialize notification service:', error);
    }
  }

  /**
   * Initialize notification channels (Android)
   */
  private async createNotificationChannels(): Promise<void> {
    if (this.channelsCreated || Platform.OS !== 'android') return;

    try {
      // ✅ Critical Alarms Channel - صوت قوي ومميز
      await notifee.createChannel({
        id: 'alarms',
        name: 'إنذارات حرجة | Critical Alarms',
        importance: AndroidImportance.HIGH,
        sound: 'default', // Using default system sound (custom sounds require .mp3 files in res/raw/)
        vibration: true,
        vibrationPattern: [300, 500, 300, 500], // اهتزاز قوي | Strong vibration
        lights: true,
        lightColor: '#FF0000',
      });

      // ✅ Battery Alerts Channel - صوت متوسط للتحذيرات
      await notifee.createChannel({
        id: 'battery',
        name: 'تنبيهات البطارية | Battery Alerts',
        importance: AndroidImportance.DEFAULT,
        sound: 'default', // Using default system sound
        vibration: true,
        vibrationPattern: [200, 300],
        lights: true,
        lightColor: '#FFA500',
      });

      // ✅ Device Status Channel - صوت خفيف للمعلومات
      await notifee.createChannel({
        id: 'offline',
        name: 'حالة الأجهزة | Device Status',
        importance: AndroidImportance.DEFAULT,
        sound: 'default', // Using default system sound
        vibration: true,
        vibrationPattern: [200, 300],
        lights: true,
        lightColor: '#808080',
      });

      // ✅ General Notifications Channel - صوت عام
      await notifee.createChannel({
        id: 'general',
        name: 'إشعارات عامة | General Notifications',
        importance: AndroidImportance.HIGH,
        sound: 'default', // Using default system sound
        vibration: true,
        vibrationPattern: [200, 300],
      });

      // Default Channel (fallback for FCM)
      await notifee.createChannel({
        id: 'default',
        name: 'الإشعارات الافتراضية | Default Notifications',
        importance: AndroidImportance.HIGH,
        sound: 'default',
        vibration: true,
        vibrationPattern: [200, 300],
      });

      this.channelsCreated = true;
      logger.log('📱 Notification channels created successfully');
    } catch (error) {
      logger.error('Failed to create notification channels:', error);
    }
  }

  /**
   * Get current notification permission status
   */
  async getPermissionStatus(): Promise<{
    status: 'granted' | 'denied' | 'blocked' | 'undetermined';
    authStatus?: number;
  }> {
    // Skip Firebase on web platform
    if ((Platform.OS as any) === 'web') {
      logger.warn('Firebase notifications not supported on web platform');
      return { status: 'denied' };
    }

    if (!Device.isDevice) {
      logger.warn('Notifications only work on physical devices');
      return { status: 'denied' };
    }

    try {
      const authStatus = await messaging().hasPermission();

      if (
        authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
        authStatus === messaging.AuthorizationStatus.PROVISIONAL
      ) {
        return { status: 'granted', authStatus };
      } else if (authStatus === messaging.AuthorizationStatus.NOT_DETERMINED) {
        return { status: 'undetermined', authStatus };
      } else {
        // DENIED or other negative status
        return { status: 'denied', authStatus };
      }
    } catch (error) {
      logger.error('Error checking notification permissions:', error);
      return { status: 'denied' };
    }
  }

  /**
   * Ensure notification permissions are granted
   * Returns permission status and whether it's granted
   * This is the main function to use before any notification action
   */
  async ensureNotificationsPermission(): Promise<{
    granted: boolean;
    status: 'granted' | 'denied' | 'blocked' | 'undetermined';
    shouldOpenSettings: boolean;
  }> {
    // Skip Firebase on web platform
    if ((Platform.OS as any) === 'web') {
      logger.warn('Firebase notifications not supported on web platform');
      return { granted: false, status: 'denied', shouldOpenSettings: false };
    }

    if (!Device.isDevice) {
      logger.warn('Notifications only work on physical devices');
      return { granted: false, status: 'denied', shouldOpenSettings: false };
    }

    try {
      // First, check current permission status
      const currentStatus = await this.getPermissionStatus();
      logger.log('📱 Current notification permission status:', currentStatus.status);

      if (currentStatus.status === 'granted') {
        return { granted: true, status: 'granted', shouldOpenSettings: false };
      }

      // If undetermined, we can request permission
      if (currentStatus.status === 'undetermined') {
        logger.log('🔔 ===== iOS PERMISSION PROMPT WILL APPEAR NOW =====');
        logger.log('📱 Permission undetermined, requesting...');

        const authStatus = await messaging().requestPermission();
        logger.log('🔔 User responded to permission prompt');
        logger.log('🔔 Auth status code:', authStatus);
        logger.log('🔔 AUTHORIZED constant:', messaging.AuthorizationStatus.AUTHORIZED);
        logger.log('🔔 PROVISIONAL constant:', messaging.AuthorizationStatus.PROVISIONAL);

        const enabled =
          authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
          authStatus === messaging.AuthorizationStatus.PROVISIONAL;

        if (enabled) {
          logger.log('✅ ===== PERMISSION GRANTED! =====');
          logger.log('✅ Notification permissions granted by user');
          return { granted: true, status: 'granted', shouldOpenSettings: false };
        } else {
          logger.warn('❌ ===== PERMISSION DENIED! =====');
          logger.warn('❌ User denied notification permissions');
          return { granted: false, status: 'denied', shouldOpenSettings: true };
        }
      }

      // If denied/blocked, user must enable in Settings
      logger.warn('⚠️ Notification permissions previously denied - user must enable in Settings');
      return { granted: false, status: 'denied', shouldOpenSettings: true };
    } catch (error) {
      logger.error('Error ensuring notification permissions:', error);
      return { granted: false, status: 'denied', shouldOpenSettings: false };
    }
  }

  /**
   * Request notification permissions (legacy - use ensureNotificationsPermission instead)
   */
  async requestPermissions(): Promise<boolean> {
    const result = await this.ensureNotificationsPermission();
    return result.granted;
  }

  // ✅ CRITICAL FIX: Prevent concurrent registration attempts
  private isRegistering = false;
  private registrationPromise: Promise<string | null> | null = null;

  /**
   * Register for push notifications and get FCM token
   */
  async registerForPushNotifications(): Promise<string | null> {
    logger.log('🚀 ===== registerForPushNotifications START =====');
    logger.log('📱 Platform:', Platform.OS);
    logger.log('📱 Is Physical Device:', Device.isDevice);

    // ✅ CRITICAL: If already registering, wait for existing promise
    if (this.isRegistering && this.registrationPromise) {
      logger.log('⏸️ Registration already in progress, waiting for result...');
      return await this.registrationPromise;
    }

    // ✅ FIX: Skip if already registered in this session (prevents duplicates)
    if (this.fcmToken && this.fcmToken.length > 0) {
      logger.log('✅ Already registered in this session, returning cached token');
      return this.fcmToken;
    }

    // Mark as registering and store promise
    this.isRegistering = true;
    this.registrationPromise = this._performRegistration();

    try {
      const result = await this.registrationPromise;
      return result;
    } finally {
      this.isRegistering = false;
      this.registrationPromise = null;
    }
  }

  private async _performRegistration(): Promise<string | null> {
    logger.log('🔧 _performRegistration: Starting actual registration...');

    // Skip Firebase on web platform
    if ((Platform.OS as any) === 'web') {
      logger.warn('Firebase notifications not supported on web platform');
      return null;
    }

    try {
      // Check permissions
      logger.log('🔐 Requesting permissions...');
      const hasPermission = await this.requestPermissions();
      logger.log('🔐 Permission result:', hasPermission);

      if (!hasPermission) {
        logger.warn('❌ Push notification permission DENIED or NOT GRANTED');
        logger.warn('Push notification permission denied');
        return null;
      }

      logger.log('✅ Permissions GRANTED - proceeding to get FCM token');

      // Create notification channels (Android)
      await this.createNotificationChannels();

      // Get FCM token with timeout
      logger.log('🔑 Requesting FCM token from Firebase...');
      const fcmToken = await Promise.race([
        messaging().getToken(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('FCM token timeout')), 10000),
        ),
      ]);

      this.fcmToken = fcmToken;

      logger.log('✅ ===== FCM TOKEN RECEIVED =====');
      logger.log('🔑 Token length:', fcmToken.length, 'chars');
      logger.log('🔑 Token format: [REDACTED for security]');

      // ✅ FIX: Unsubscribe first to avoid duplicate subscriptions
      try {
        await messaging().unsubscribeFromTopic('iotedge-all');
        logger.log('🚫 Unsubscribed from iotedge-all (cleanup before subscribe)');
      } catch (unsubError) {
        logger.warn('Failed to unsubscribe (may not be subscribed yet):', unsubError);
        // Don't fail registration if unsubscribe fails
      }

      // Subscribe to global topic for broadcast notifications
      try {
        await messaging().subscribeToTopic('iotedge-all');
        logger.log('📢 Subscribed to iotedge-all topic');
      } catch (topicError) {
        logger.warn('Failed to subscribe to topic:', topicError);
        // Don't fail registration if topic subscription fails
      }

      // Handle foreground messages (skip on web)
      if (Platform.OS !== 'web') {
        // ✅ FIX: Remove previous listener before registering a new one.
        // Without this, each logout/login cycle registers an additional onMessage listener,
        // causing the same FCM message to trigger N notifications after N login cycles.
        if (this.messageListenerUnsubscribe) {
          this.messageListenerUnsubscribe();
          logger.log('🗑️ Removed previous onMessage listener (preventing duplicate)');
        }

        this.messageListenerUnsubscribe = messaging().onMessage(async (remoteMessage) => {
          logger.log('📬 Foreground notification received:', remoteMessage);

          // ✅ FIX: Handle dismiss_alarm messages
          if (remoteMessage.data?.type === 'dismiss_alarm') {
            const alarmId =
              typeof remoteMessage.data.alarmId === 'string'
                ? remoteMessage.data.alarmId
                : String(remoteMessage.data.alarmId);
            logger.log(`🗑️ Received dismiss request for alarm: ${alarmId}`);
            await this.dismissAlarmNotification(alarmId);
            return;
          }

          await this.displayNotification(remoteMessage);
        });

        // Handle token refresh
        if (this.tokenRefreshUnsubscribe) {
          this.tokenRefreshUnsubscribe();
          logger.log('🗑️ Removed previous onTokenRefresh listener');
        }
        this.tokenRefreshUnsubscribe = messaging().onTokenRefresh(async (token) => {
          logger.log('🔄 FCM token refreshed');
          this.fcmToken = token;
          await this.updatePushToken(token);
        });
      }

      // Handle notification interaction (foreground)
      notifee.onForegroundEvent(({ type, detail }) => {
        if (type === EventType.PRESS && detail.notification?.data) {
          logger.log('👆 Notification tapped (foreground):', detail.notification.data);
          this.tapCallback?.(detail.notification.data);
        }
      });

      logger.log('🎉 ===== registerForPushNotifications SUCCESS =====');
      return this.fcmToken;
    } catch (error) {
      logger.error('💥 ===== registerForPushNotifications FAILED =====');
      logger.error('Error details:', error);
      logger.error('Error registering for push notifications:', error);
      return null;
    }
  }

  /**
   * Display notification using notifee
   */
  async displayNotification(remoteMessage: any): Promise<void> {
    const { notification, data } = remoteMessage;

    // ✅ FIX: Support both notification-payload and data-only messages.
    // When the server sends data-only FCM messages (no 'notification' field),
    // fall back to data.title / data.body embedded in the payload.
    const title = notification?.title || (data?.title as string);
    const body = notification?.body || (data?.body as string);

    if (!title && !body) {
      logger.warn('No notification content in message (no title/body)');
      return;
    }

    // Check preferences
    const prefs = await this.getPreferences();
    const shouldShow = await this.shouldShowNotification();

    if (!shouldShow) {
      logger.log('🔕 Notification blocked by Quiet Hours');
      return;
    }

    // Check notification type preferences
    const notificationType = data?.type;
    if (notificationType === 'alarm' && !prefs.alarmsEnabled) return;
    if (notificationType === 'low_battery' && !prefs.lowBatteryEnabled) return;
    if (notificationType === 'device_offline' && !prefs.deviceOfflineEnabled) return;

    try {
      // Determine channel based on type
      let channelId = 'general';
      if (notificationType === 'alarm') {
        channelId = data?.severity === 'critical' ? 'alarms' : 'general';
      } else if (notificationType === 'low_battery') {
        channelId = 'battery';
      } else if (notificationType === 'device_offline') {
        channelId = 'offline';
      }

      // ✅ Use alarmId as stable notification ID — ensures that if FCM and background task
      // both try to show the same alarm, notifee updates the existing notification instead
      // of creating a duplicate.
      const stableId = (data?.alarmId as string) || undefined;

      // Display notification
      await notifee.displayNotification({
        id: stableId,
        title,
        body,
        data,
        android: {
          channelId,
          pressAction: {
            id: 'default',
          },
          // ✅ Sound is defined in channel configuration (alarm_critical, alarm_warning, alarm_info)
          vibrationPattern: [300, 500, 300, 500],
          importance: AndroidImportance.HIGH,
          smallIcon: 'ic_notification',
        },
        ios: {
          sound: 'default',
          // ✅ timeSensitive breaks through Focus mode without Critical Alerts entitlement
          // critical:true requires Apple approval + entitlement — NOT used here
          interruptionLevel: channelId === 'alarms' ? 'timeSensitive' : 'active',
        },
      });

      logger.log('✅ Notification displayed successfully');
    } catch (error) {
      logger.error('Failed to display notification:', error);
    }
  }

  /**
   * Send push token to backend
   */
  async updatePushToken(token: string): Promise<boolean> {
    try {
      const tokens = await getStoredTokens();
      if (!tokens) return false;

      const response = await fetch(`${API_BASE_URL}/users/me/push-token`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.accessToken}`,
        },
        body: JSON.stringify({
          pushToken: token,
          platform: Platform.OS,
          tokenType: 'fcm', // Firebase Cloud Messaging
        }),
      });

      if (response.ok) {
        logger.log('✅ FCM token registered with backend');
        return true;
      } else {
        logger.error('Failed to register FCM token:', response.status);
        return false;
      }
    } catch (error) {
      logger.error('Failed to update push token:', error);
      return false;
    }
  }

  /**
   * Unsubscribe all Firebase messaging listeners (called on logout)
   */
  cleanupListeners(): void {
    if (this.messageListenerUnsubscribe) {
      this.messageListenerUnsubscribe();
      this.messageListenerUnsubscribe = null;
      logger.log('🗑️ onMessage listener removed on logout');
    }
    if (this.tokenRefreshUnsubscribe) {
      this.tokenRefreshUnsubscribe();
      this.tokenRefreshUnsubscribe = null;
      logger.log('🗑️ onTokenRefresh listener removed on logout');
    }
  }

  /**
   * Remove push token from backend (called on logout)
   * ✅ FIX: Prevents duplicate notifications when switching accounts
   */
  async removePushToken(): Promise<boolean> {
    try {
      const tokens = await getStoredTokens();
      if (!tokens) {
        logger.log('ℹ️ No auth tokens found - skipping push token removal');
        return true;
      }

      logger.log('🗑️ Removing FCM token from backend...');

      const response = await fetch(`${API_BASE_URL}/users/me/push-token`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.accessToken}`,
        },
      });

      if (response.ok || response.status === 404) {
        logger.log('✅ FCM token removed from backend');
        this.fcmToken = null;
        return true;
      } else {
        logger.error('⚠️ Failed to remove FCM token:', response.status);
        return false;
      }
    } catch (error) {
      logger.error('❌ Failed to remove push token:', error);
      return false;
    }
  }

  /**
   * Get notification preferences
   */
  async getPreferences(): Promise<NotificationPreferences> {
    try {
      const stored = await AsyncStorage.getItem('notification_preferences');
      if (stored) {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
      }
      return DEFAULT_PREFERENCES;
    } catch (error) {
      logger.error('Failed to get notification preferences:', error);
      return DEFAULT_PREFERENCES;
    }
  }

  /**
   * Save notification preferences
   */
  async savePreferences(preferences: Partial<NotificationPreferences>): Promise<void> {
    try {
      const current = await this.getPreferences();
      const updated = { ...current, ...preferences };
      await AsyncStorage.setItem('notification_preferences', JSON.stringify(updated));
      logger.log('✅ Notification preferences saved');
    } catch (error) {
      logger.error('Failed to save notification preferences:', error);
    }
  }

  /**
   * Check if notifications should be shown (quiet hours check)
   */
  async shouldShowNotification(): Promise<boolean> {
    const prefs = await this.getPreferences();

    if (!prefs.quietHoursEnabled) return true;

    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const [startHour, startMin] = prefs.quietHoursStart.split(':').map(Number);
    const [endHour, endMin] = prefs.quietHoursEnd.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    // Handle overnight quiet hours (e.g., 22:00 to 07:00)
    // isInQuietHours = true means we should NOT show notification
    let isInQuietHours: boolean;
    if (startMinutes > endMinutes) {
      // Overnight: quiet if current >= start OR current < end
      isInQuietHours = currentMinutes >= startMinutes || currentMinutes < endMinutes;
    } else {
      // Same-day: quiet if current >= start AND current < end
      isInQuietHours = currentMinutes >= startMinutes && currentMinutes < endMinutes;
    }

    return !isInQuietHours;
  }

  /**
   * Schedule a local notification for alarm
   */
  async scheduleAlarmNotification(
    deviceName: string,
    severity: 'critical' | 'warning' | 'info',
    message: string,
    deviceId: string,
  ): Promise<void> {
    const prefs = await this.getPreferences();
    if (!prefs.alarmsEnabled) return;

    const shouldShow = await this.shouldShowNotification();
    if (!shouldShow) return;

    try {
      const channelId = severity === 'critical' ? 'alarms' : 'general';
      const icon = severity === 'critical' ? '🚨' : severity === 'warning' ? '⚠️' : 'ℹ️';

      await notifee.displayNotification({
        title: `${icon} ${deviceName}`,
        body: message,
        data: {
          type: 'alarm',
          deviceId,
          severity,
        },
        android: {
          channelId,
          pressAction: { id: 'default' },
          // Sound configured in channel (using default Android system sound)
          vibrationPattern: prefs.vibrationEnabled ? [300, 500, 300, 500] : undefined,
          importance: severity === 'critical' ? AndroidImportance.HIGH : AndroidImportance.DEFAULT,
          smallIcon: 'ic_notification',
        },
        ios: {
          sound: prefs.soundEnabled ? 'default' : undefined,
          // ✅ timeSensitive breaks through Focus mode — no Apple approval required
          interruptionLevel: severity === 'critical' ? 'timeSensitive' : 'active',
        },
      });

      logger.log(`✅ Local alarm notification scheduled: ${severity}`);
    } catch (error) {
      logger.error('Failed to schedule alarm notification:', error);
    }
  }

  /**
   * Schedule a local notification for low battery
   */
  async scheduleLowBatteryNotification(
    deviceName: string,
    batteryLevel: number,
    deviceId: string,
  ): Promise<void> {
    const prefs = await this.getPreferences();
    if (!prefs.lowBatteryEnabled) return;

    const shouldShow = await this.shouldShowNotification();
    if (!shouldShow) return;

    try {
      await notifee.displayNotification({
        title: `🔋 ${deviceName}`,
        body: `البطارية منخفضة: ${batteryLevel.toFixed(1)}V\nLow Battery: ${batteryLevel.toFixed(
          1,
        )}V`,
        data: {
          type: 'low_battery',
          deviceId,
          batteryLevel,
        },
        android: {
          channelId: 'battery',
          pressAction: { id: 'default' },
          // Sound configured in channel (default system sound)
          vibrationPattern: prefs.vibrationEnabled ? [200, 300] : undefined,
          smallIcon: 'ic_notification',
        },
        ios: {
          sound: prefs.soundEnabled ? 'default' : undefined,
        },
      });

      logger.log('✅ Low battery notification scheduled');
    } catch (error) {
      logger.error('Failed to schedule low battery notification:', error);
    }
  }

  /**
   * Schedule a local notification for device offline
   */
  async scheduleDeviceOfflineNotification(
    deviceName: string,
    lastSeen: Date,
    deviceId: string,
  ): Promise<void> {
    const prefs = await this.getPreferences();
    if (!prefs.deviceOfflineEnabled) return;

    const shouldShow = await this.shouldShowNotification();
    if (!shouldShow) return;

    try {
      const timeDiff = Date.now() - lastSeen.getTime();
      const minutes = Math.floor(timeDiff / 60000);

      await notifee.displayNotification({
        title: `📡 ${deviceName}`,
        body: `الجهاز غير متصل منذ ${minutes} دقيقة\nDevice offline for ${minutes} minutes`,
        data: {
          type: 'device_offline',
          deviceId,
          lastSeen: lastSeen.toISOString(),
        },
        android: {
          channelId: 'offline',
          pressAction: { id: 'default' },
          // Sound configured in channel (default system sound)
          vibrationPattern: prefs.vibrationEnabled ? [200, 300] : undefined,
          smallIcon: 'ic_notification',
        },
        ios: {
          sound: prefs.soundEnabled ? 'default' : undefined,
        },
      });

      logger.log('✅ Device offline notification scheduled');
    } catch (error) {
      logger.error('Failed to schedule device offline notification:', error);
    }
  }

  /**
   * Cancel all scheduled notifications
   */
  async cancelAllNotifications(): Promise<void> {
    try {
      await notifee.cancelAllNotifications();
      logger.log('✅ All notifications cancelled');
    } catch (error) {
      logger.error('Failed to cancel notifications:', error);
    }
  }

  /**
   * ✅ NEW: Dismiss specific alarm notification by alarmId
   * Called when alarm is acknowledged or resolved on another device
   */
  async dismissAlarmNotification(alarmId: string): Promise<void> {
    try {
      logger.log(`🗑️ Dismissing alarm notification: ${alarmId}`);

      // Get all displayed notifications
      const displayedNotifications = await notifee.getDisplayedNotifications();

      // Find notifications matching this alarmId
      for (const notification of displayedNotifications) {
        const notificationAlarmId = notification.notification?.data?.alarmId;
        if (notificationAlarmId && String(notificationAlarmId) === String(alarmId)) {
          const notificationId = notification.id;
          if (notificationId) {
            await notifee.cancelNotification(notificationId);
            logger.log(`✅ Cancelled notification ${notificationId} for alarm ${alarmId}`);
          }
        }
      }

      // Also update badge count
      const remainingCount = await notifee.getDisplayedNotifications();
      await this.setBadgeCount(remainingCount.length);
    } catch (error) {
      logger.error('Failed to dismiss alarm notification:', error);
    }
  }

  /**
   * Get badge count
   */
  async getBadgeCount(): Promise<number> {
    try {
      return await notifee.getBadgeCount();
    } catch (error) {
      logger.error('Failed to get badge count:', error);
      return 0;
    }
  }

  /**
   * Set badge count
   */
  async setBadgeCount(count: number): Promise<void> {
    try {
      await notifee.setBadgeCount(count);
      logger.log(`✅ Badge count set to ${count}`);
    } catch (error) {
      logger.error('Failed to set badge count:', error);
    }
  }

  /**
   * Clear badge
   */
  async clearBadge(): Promise<void> {
    await this.setBadgeCount(0);
  }

  /**
   * Schedule a test notification (for testing purposes)
   */
  async scheduleTestNotification(
    severity: 'info' | 'warning' | 'critical' = 'warning',
  ): Promise<void> {
    const titles = {
      info: 'إشعار معلوماتي ℹ️',
      warning: 'تحذير من درجة الحرارة ⚠️',
      critical: 'تنبيه حرج! 🚨',
    };

    const bodies = {
      info: 'تم إضافة جهاز جديد إلى النظام',
      warning: 'تجاوزت درجة الحرارة الحد المسموح - ثلاجة الأدوية',
      critical: 'انقطاع كامل في التبريد! يتطلب تدخل فوري',
    };

    const channelIds = {
      info: 'general',
      warning: 'general',
      critical: 'alarms',
    };

    try {
      await notifee.displayNotification({
        title: titles[severity],
        body: bodies[severity],
        data: {
          alarmId: `test-${Date.now()}`,
          severity,
          deviceId: 'test-device-id',
          deviceName: 'ثلاجة الأدوية - قسم الطوارئ',
        },
        android: {
          channelId: channelIds[severity],
          pressAction: { id: 'default' },
          sound: 'default',
          vibrationPattern: [300, 500],
          importance: severity === 'critical' ? AndroidImportance.HIGH : AndroidImportance.DEFAULT,
        },
        ios: {
          sound: 'default',
          // ✅ timeSensitive breaks through Focus mode — no Apple approval required
          interruptionLevel: severity === 'critical' ? 'timeSensitive' : 'active',
        },
      });

      logger.log(`✅ Test notification displayed: ${severity}`);
    } catch (error) {
      logger.error('Failed to display test notification:', error);
    }
  }

  /**
   * Setup foreground notification tap handler
   * Call this from App.tsx after navigation is ready
   */
  setupNotificationTapHandler(onTap: (data: any) => void): void {
    // Store the callback — the foreground handler registered during
    // registerForPushNotifications() will call it when tapped
    this.tapCallback = onTap;
    logger.log('✅ Notification tap handler registered');
  }
}

// Create singleton instance
const notificationService = new NotificationService();

// Track if background handler is already registered
/**
 * Flag indicating whether the background notification handler has been registered.
 * Prevents multiple registrations of the background message handler.
 * @internal
 */
let backgroundHandlerRegistered = false;

/**
 * Register background message handler
 * This MUST be called at the top level (outside of any component)
 */
export function registerBackgroundMessageHandler() {
  // Skip if already registered
  if (backgroundHandlerRegistered) {
    logger.log('⚠️ Background message handler already registered, skipping...');
    return;
  }

  // Skip on web platform
  if (Platform.OS === 'web') {
    logger.warn('Background message handler not supported on web platform');
    return;
  }

  try {
    // Handle background messages
    messaging().setBackgroundMessageHandler(async (remoteMessage) => {
      logger.log('📬 Background notification received:', remoteMessage);

      // ✅ FIX: Handle dismiss_alarm messages in background
      if (remoteMessage.data?.type === 'dismiss_alarm') {
        const alarmId =
          typeof remoteMessage.data.alarmId === 'string'
            ? remoteMessage.data.alarmId
            : String(remoteMessage.data.alarmId);
        logger.log(`🗑️ [Background] Received dismiss request for alarm: ${alarmId}`);

        try {
          // Get all displayed notifications
          const displayedNotifications = await notifee.getDisplayedNotifications();

          // Find and cancel notifications matching this alarmId
          for (const notification of displayedNotifications) {
            const notificationAlarmId = notification.notification?.data?.alarmId;
            if (notificationAlarmId && String(notificationAlarmId) === String(alarmId)) {
              const notificationId = notification.id;
              if (notificationId) {
                await notifee.cancelNotification(notificationId);
                logger.log(
                  `✅ [Background] Cancelled notification ${notificationId} for alarm ${alarmId}`,
                );
              }
            }
          }
        } catch (error) {
          logger.error('[Background] Failed to dismiss alarm notification:', error);
        }

        return; // Don't display notification
      }

      const { notification, data } = remoteMessage;

      // ✅ FIX: On Android, FCM messages that include a 'notification' field are
      // automatically shown by the OS in the system tray. Calling notifee here would
      // display the SAME notification a second time (double notification bug).
      // For Android with notification field: skip notifee (OS already handled display).
      // For iOS or data-only messages: use notifee for proper channel/sound/interruption.
      if (Platform.OS === 'android' && notification) {
        logger.log(
          '📬 [Background] Android: OS already displayed notification, skipping notifee re-display',
        );
        return;
      }

      // Support both notification-payload and data-only messages
      const title = notification?.title || (data?.title as string) || '';
      const body = notification?.body || (data?.body as string) || '';
      if (!title && !body) return;

      try {
        // Determine channel based on type
        let channelId = 'default';
        const notificationType = data?.type;

        if (notificationType === 'alarm') {
          channelId = data?.severity === 'critical' ? 'alarms' : 'general';
        } else if (notificationType === 'low_battery') {
          channelId = 'battery';
        } else if (notificationType === 'device_offline') {
          channelId = 'offline';
        }

        // ✅ Use alarmId as stable notification ID to prevent duplicates across FCM + background task
        const stableId = (data?.alarmId as string) || undefined;

        // Display notification with sound
        await notifee.displayNotification({
          id: stableId,
          title,
          body,
          data: data as Record<string, string>,
          android: {
            channelId,
            pressAction: { id: 'default' },
            sound: 'default',
            vibrationPattern: [300, 500, 300, 500],
            importance: AndroidImportance.HIGH,
            smallIcon: 'ic_notification',
          },
          ios: {
            sound: 'default',
            // ✅ timeSensitive breaks through Focus mode — no Apple approval required
            interruptionLevel: channelId === 'alarms' ? 'timeSensitive' : 'active',
          },
        });
      } catch (error) {
        logger.error('Background notification error:', error);
      }
    });

    // Handle background notification events (notifee)
    // ✅ FIX: Save pending navigation to AsyncStorage; App.tsx reads it when ready
    notifee.onBackgroundEvent(async ({ type, detail }) => {
      const { notification } = detail;

      if (type === EventType.PRESS && notification?.data) {
        logger.log('👆 Background notifee tapped, saving pending nav:', notification.data);
        await AsyncStorage.setItem('@pending_notification_nav', JSON.stringify(notification.data));
      }
    });

    backgroundHandlerRegistered = true;
    logger.log('✅ Background message handler registered successfully');
  } catch (error) {
    logger.error('❌ Failed to register background message handler:', error);
    // Don't crash the app if registration fails
  }
}

export default notificationService;
